import { useState } from "react";
import { LeaderboardChart } from "../components/LeaderboardChart";
import { PageHeader } from "../components/PageHeader";
import { TeamBadge } from "../components/TeamBadge";
import { useApp, type PlayerLite, type State } from "../state";
import { formChipClass } from "../util";

const W = 360;
const H = 200;
const PAD_L = 30;
const PAD_R = 42;
const PAD_T = 20;
const PAD_B = 28;
const INNER_W = W - PAD_L - PAD_R;
const INNER_H = H - PAD_T - PAD_B;

export function Stats() {
  const { state, me } = useApp();
  // One spotlight drives every visual on the page: the two race charts and
  // the heatmap all highlight the same player. Defaults to the signed-in
  // player so "how am I doing?" is the first read.
  const [spotlighted, setSpotlighted] = useState<string | null>(me?.id ?? null);

  const completed = state.matches
    .filter((m) => m.status === "final" || m.status === "final_pens")
    .sort((a, b) => a.kickoff_utc.localeCompare(b.kickoff_utc));

  // Legend sorted by current rank — mirrors the Big Table, so finding a rival
  // is the same motion on both pages.
  const legend = state.leaderboard
    .map((e) => ({ entry: e, player: state.players.find((p) => p.id === e.player_id) }))
    .filter((x): x is { entry: State["leaderboard"][number]; player: PlayerLite } => Boolean(x.player));

  return (
    <div className="page stats-page">
      <PageHeader
        title="The VAR Room"
        tone="purple"
        sub={`${completed.length} graded matches · tap a player to follow them everywhere`}
      />

      <div className="stats-legend" role="group" aria-label="Spotlight a player across all charts">
        {legend.map(({ entry, player }) => {
          const isOn = spotlighted === player.id;
          return (
            <button
              key={player.id}
              type="button"
              className={`lb-chart__chip ${isOn ? "is-on" : ""}`}
              onClick={() => setSpotlighted(isOn ? null : player.id)}
              aria-pressed={isOn}
            >
              <span className="lb-chart__dot" style={{ background: player.color }} aria-hidden="true" />
              <span>#{entry.rank} {player.name}</span>
            </button>
          );
        })}
      </div>

      <LeaderboardChart
        history={state.history}
        players={state.players}
        matches={state.matches}
        spotlighted={spotlighted}
      />
      <RankRaceChart
        history={state.history}
        players={state.players}
        matches={state.matches}
        spotlighted={spotlighted}
      />
      <MatchHeatmap state={state} completed={completed} spotlighted={spotlighted} />
      <SharpestPicks state={state} />
      <BiggestSwings state={state} />

      <div className="card stats-note">
        <span>📡</span>
        <div>
          <strong>Live from the data room</strong>
          <p>Corrections to scores, predictions, or player history automatically flow into these charts.</p>
        </div>
      </div>
    </div>
  );
}

function RankRaceChart({
  history,
  players,
  matches,
  spotlighted,
}: {
  history: State["history"];
  players: PlayerLite[];
  matches: State["matches"];
  spotlighted: string | null;
}) {
  if (history.length === 0) {
    return (
      <div className="lb-chart lb-chart--empty">
        <div className="section-label">RANK RACE</div>
        <p>The race line appears once the first match is graded.</p>
      </div>
    );
  }

  const matchById = new Map(matches.map((m) => [m.id, m]));
  const maxRank = Math.max(1, ...history.flatMap((h) => Object.values(h.ranks)));
  const xFor = (i: number) =>
    history.length === 1
      ? PAD_L + INNER_W * 0.5
      : PAD_L + (INNER_W * i) / (history.length - 1);
  const yFor = (rank: number) =>
    maxRank === 1 ? PAD_T + INNER_H * 0.5 : PAD_T + ((rank - 1) * INNER_H) / (maxRank - 1);
  const latest = history[history.length - 1];

  const series = players.map((p) => {
    let prevRank = maxRank;
    const points = history.map((h, i) => {
      const rank = h.ranks[p.id] ?? prevRank;
      prevRank = rank;
      return { x: xFor(i), y: yFor(rank), rank };
    });
    return { player: p, points, currentRank: latest.ranks[p.id] ?? null };
  });

  // Round boundaries, same treatment as the points race.
  const rounds: Array<{ from: number; to: number; round: string }> = [];
  history.forEach((h, i) => {
    const round = matchById.get(h.match_id)?.round ?? "?";
    const last = rounds[rounds.length - 1];
    if (last && last.round === round) last.to = i;
    else rounds.push({ from: i, to: i, round });
  });

  const ordered = [...series].sort((a, b) => {
    const aSpot = a.player.id === spotlighted ? 1 : 0;
    const bSpot = b.player.id === spotlighted ? 1 : 0;
    return aSpot - bSpot;
  });

  return (
    <div className="lb-chart">
      <div className="lb-chart__head">
        <div className="section-label">RANK RACE</div>
        <div className="lb-chart__sub">Every crossing is an overtake · #1 stays on top</div>
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" role="img" aria-label="Player ranks over time">
        <line x1={PAD_L} x2={PAD_L} y1={PAD_T} y2={PAD_T + INNER_H} stroke="rgba(42,26,10,0.18)" strokeWidth={1} />
        <line x1={PAD_L} x2={PAD_L + INNER_W} y1={PAD_T + INNER_H} y2={PAD_T + INNER_H} stroke="rgba(42,26,10,0.18)" strokeWidth={1} />
        <text x={PAD_L - 6} y={PAD_T + 5} fontSize="9" textAnchor="end" fill="#78716c">#1</text>
        <text x={PAD_L - 6} y={PAD_T + INNER_H + 3} fontSize="9" textAnchor="end" fill="#78716c">#{maxRank}</text>

        {rounds.map((r, ri) => {
          const midX = (xFor(r.from) + xFor(r.to)) / 2;
          return (
            <g key={`round-${r.round}-${r.from}`}>
              {ri > 0 && (
                <line
                  x1={(xFor(r.from - 1) + xFor(r.from)) / 2}
                  x2={(xFor(r.from - 1) + xFor(r.from)) / 2}
                  y1={PAD_T}
                  y2={PAD_T + INNER_H}
                  stroke="rgba(42,26,10,0.22)"
                  strokeWidth={1}
                  strokeDasharray="3 3"
                />
              )}
              <text x={midX} y={PAD_T - 8} fontSize="8.5" textAnchor="middle" fontWeight="800" fill="#a8a29e" letterSpacing="0.06em">
                {r.round}
              </text>
            </g>
          );
        })}

        {ordered.map(({ player, points }) => {
          const isSpot = player.id === spotlighted;
          const dimmed = spotlighted && !isSpot;
          return (
            <polyline
              key={player.id}
              points={points.map((p) => `${p.x},${p.y}`).join(" ")}
              stroke={player.color}
              strokeWidth={isSpot ? 2.5 : 1.4}
              fill="none"
              opacity={dimmed ? 0.18 : isSpot ? 0.95 : 0.55}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <title>{`${player.name}: current rank #${latest.ranks[player.id] ?? "—"}`}</title>
            </polyline>
          );
        })}

        {series.map(({ player, points, currentRank }) => {
          if (currentRank == null) return null;
          const last = points[points.length - 1];
          const isSpot = player.id === spotlighted;
          const dimmed = spotlighted && !isSpot;
          const pillW = currentRank >= 10 ? 22 : 18;
          return (
            <g key={`rank-race-label-${player.id}`} opacity={dimmed ? 0.35 : 1}>
              <circle cx={last.x} cy={last.y} r={isSpot ? 3.4 : 2.4} fill={player.color} />
              <rect x={last.x + 6} y={last.y - 6.5} width={pillW} height={12} rx={6} fill="#fff8ec" stroke={player.color} strokeWidth={isSpot ? 1.4 : 1} />
              <text x={last.x + 6 + pillW / 2} y={last.y + 3.2} fontSize="8" textAnchor="middle" fontWeight="800" fill="#2a1a0a">#{currentRank}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function MatchHeatmap({
  state,
  completed,
  spotlighted,
}: {
  state: State;
  completed: State["matches"];
  spotlighted: string | null;
}) {
  const recentByPlayer = new Map(
    Object.entries(state.profiles).map(([playerId, profile]) => [
      playerId,
      new Map(profile.recent_picks.map((p) => [p.match_id, p])),
    ]),
  );

  // Match ids that start a new round — get a separator border so the grid
  // reads in tournament phases instead of one long strip.
  const newRoundIds = new Set<string>();
  completed.forEach((m, i) => {
    if (i > 0 && completed[i - 1].round !== m.round) newRoundIds.add(m.id);
  });

  return (
    <div className="card stats-heatmap-card">
      <div className="stats-section-head">
        <div>
          <div className="section-label">MATCH HEATMAP</div>
          <p>Every row is a player, every square is a completed match.</p>
        </div>
        <span className="pill">Gold = max</span>
      </div>

      {completed.length === 0 ? (
        <p className="stats-empty">No completed matches yet.</p>
      ) : (
        <div className="stats-heatmap" style={{ gridTemplateColumns: `64px repeat(${completed.length}, minmax(22px, 1fr))` }}>
          <div />
          {completed.map((m, index) => (
            <div
              key={m.id}
              className={`stats-heatmap__match ${newRoundIds.has(m.id) ? "stats-heatmap__newround" : ""}`}
              title={`${m.round} · ${m.home} vs ${m.away}`}
            >
              {index + 1}
            </div>
          ))}
          {state.leaderboard.map((entry) => {
            const player = state.players.find((p) => p.id === entry.player_id);
            const picks = recentByPlayer.get(entry.player_id);
            const dimmed = spotlighted && spotlighted !== entry.player_id;
            return (
              <div
                className={`stats-heatmap__row ${dimmed ? "stats-heatmap__row--dim" : ""} ${spotlighted === entry.player_id ? "stats-heatmap__row--spot" : ""}`}
                key={entry.player_id}
              >
                <div className="stats-heatmap__player">
                  <span className="stats-heatmap__swatch" style={{ background: entry.color }} />
                  <span>{entry.name}</span>
                </div>
                {completed.map((m) => {
                  const recent = picks?.get(m.id);
                  const points = recent?.points ?? state.history.find((h) => h.match_id === m.id)?.points_gained[entry.player_id] ?? 0;
                  const max = recent?.max_points ?? m.max_points;
                  const exact = Boolean(recent?.pick && recent.actual && recent.pick.home === recent.actual.home && recent.pick.away === recent.actual.away && !recent.missing_award);
                  return (
                    <span
                      key={m.id}
                      className={`stats-heatmap__cell ${formChipClass(points, max, recent?.missing_award)} ${exact ? "stats-heatmap__cell--exact" : ""} ${newRoundIds.has(m.id) ? "stats-heatmap__newround" : ""}`}
                      title={`${player?.name ?? entry.name}: ${points}/${max} · ${m.home} ${m.away}${recent?.missing_award ? " · no pick" : ""}`}
                    >
                      {points}
                    </span>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      <div className="stats-match-key">
        {completed.map((m, index) => (
          <span key={m.id}>
            {index + 1}: <TeamBadge code={m.home} compact /> <TeamBadge code={m.away} compact />
          </span>
        ))}
      </div>
    </div>
  );
}

function SharpestPicks({ state }: { state: State }) {
  const topExact = [...state.leaderboard].sort((a, b) => b.exact_count - a.exact_count || a.rank - b.rank)[0];
  const topGd = [...state.leaderboard].sort((a, b) => b.goal_difference_count - a.goal_difference_count || a.rank - b.rank)[0];
  const cleanest = [...state.leaderboard].sort((a, b) => a.score_error - b.score_error || a.rank - b.rank)[0];
  const mostAccurate = [...state.leaderboard].sort((a, b) => state.profiles[b.player_id].accuracy - state.profiles[a.player_id].accuracy || a.rank - b.rank)[0];

  const cards = [
    topExact && { label: "Bullseye boss", value: topExact.name, stat: `${topExact.exact_count} exact` },
    topGd && { label: "GD whisperer", value: topGd.name, stat: `${topGd.goal_difference_count} exact GD` },
    cleanest && { label: "Closest radar", value: cleanest.name, stat: `${cleanest.score_error} total error` },
    mostAccurate && { label: "Call machine", value: mostAccurate.name, stat: `${Math.round(state.profiles[mostAccurate.player_id].accuracy * 100)}% accuracy` },
  ].filter(Boolean);

  return (
    <div>
      <div className="section-label">SHARPEST PICKS</div>
      <div className="stats-mini-grid">
        {cards.map((card) => (
          <div className="card stats-mini-card" key={card.label}>
            <div className="stats-mini-card__label">{card.label}</div>
            <strong>{card.value}</strong>
            <span>{card.stat}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function BiggestSwings({ state }: { state: State }) {
  const matchById = new Map(state.matches.map((m) => [m.id, m]));
  const playerById = new Map(state.players.map((p) => [p.id, p]));
  const swings: Array<{ player: PlayerLite; matchId: string; delta: number; from: number; to: number }> = [];

  for (let i = 1; i < state.history.length; i += 1) {
    const prev = state.history[i - 1];
    const current = state.history[i];
    for (const player of state.players) {
      const from = prev.ranks[player.id];
      const to = current.ranks[player.id];
      if (!from || !to || from === to) continue;
      swings.push({ player, matchId: current.match_id, delta: from - to, from, to });
    }
  }

  const top = swings
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta) || b.delta - a.delta)
    .slice(0, 6);

  return (
    <div className="card stats-swings">
      <div className="section-label">BIGGEST SWINGS</div>
      {top.length === 0 ? (
        <p className="stats-empty">No rank jumps yet. The chaos is warming up.</p>
      ) : (
        top.map((swing) => {
          const match = matchById.get(swing.matchId);
          const player = playerById.get(swing.player.id) ?? swing.player;
          return (
            <div className="stats-swing-row" key={`${swing.matchId}-${swing.player.id}-${swing.from}-${swing.to}`}>
              <span className={`chip ${swing.delta > 0 ? "chip--up" : "chip--down"}`}>
                {swing.delta > 0 ? "↑" : "↓"}{Math.abs(swing.delta)}
              </span>
              <div>
                <strong>{player.name}</strong>
                <span>
                  #{swing.from} → #{swing.to}
                  {match && <> · <TeamBadge code={match.home} compact /> <TeamBadge code={match.away} compact /></>}
                </span>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
